/**
 * WolParts.tsx — small server-rendered pieces shared by the ticker and the
 * wall: the highlight renderer and the avatar.
 *
 * Highlights come from the feed's `text.segments` — pf-platform's own parser
 * (markedTextToSegments, lib/db/testimonial-feed.ts) has already turned the
 * stored marks into plain {text, bold, italic, highlight} runs. We only map
 * runs to elements; nothing is ever rendered as raw HTML.
 */
import type { WolSegment } from '@/lib/wall-of-love-feed';

export function Segments({ segments, fallback }: { segments: WolSegment[]; fallback: string }) {
  if (segments.length === 0) return <>{fallback}</>;
  return (
    <>
      {segments.map((s, i) => {
        let node: React.ReactNode = s.text;
        if (s.italic) node = <em>{node}</em>;
        if (s.bold) node = <strong>{node}</strong>;
        if (s.highlight) node = <mark>{node}</mark>;
        return <span key={i}>{node}</span>;
      })}
    </>
  );
}

/** First + last initial, 2 characters — same rule as PM12Quote's initialsOf. */
export function initialsOf(name: string): string {
  const parts = name.replace(/^@/, '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = Array.from(parts[0])[0] ?? '';
  const last = parts.length > 1 ? Array.from(parts[parts.length - 1])[0] ?? '' : '';
  return (first + last).toUpperCase();
}

export function Avatar({ url, name, className }: { url: string | null; name: string; className: string }) {
  if (url) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img className={className} src={url} alt="" width={64} height={64} loading="lazy" decoding="async" />
    );
  }
  return (
    <span className={`${className} ${className}--initials`} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}

const DATE_FMT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });

/** "Oct 23, 2023" — Senja's date format, fixed to UTC so server and client agree. */
export function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : DATE_FMT.format(d);
}
